import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import TCBadge from "@/components/TCBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AlertTriangle, PackageX, ArrowDownToLine, ArrowUpFromLine, Archive, ShoppingCart, DollarSign } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL as API } from "@/lib/api";
const STOCK_MIN = 10;

const FamiliaSelect = ({ value, onChange, familias, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-7 w-40 text-xs" data-testid={testId}>
      <SelectValue placeholder="General" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">General</SelectItem>
      {familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
    </SelectContent>
  </Select>
);

const DashboardOperativo = ({ globalTC }) => {
  const [products, setProducts] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipoCambio, setTipoCambio] = useState(9.4);
  const [periodoView, setPeriodoView] = useState("dia");
  const [famKpis, setFamKpis] = useState("__all__");
  const [famUrgencias, setFamUrgencias] = useState("__all__");
  const [famTendencia, setFamTendencia] = useState("__all__");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, eRes, fRes] = await Promise.all([
        axios.get(`${API}/api/products`),
        axios.get(`${API}/api/entregas/all`),
        axios.get(`${API}/api/filters`),
      ]);
      setProducts(pRes.data);
      setEntregas(eRes.data.entregas || []);
      setFamilias(fRes.data.familias || []);
    } catch {
      toast.error("Error al cargar dashboard operativo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filterByFam = (arr, fam, field = "familia") =>
    fam === "__all__" ? arr : arr.filter(x => x[field] === fam);

  // KPIs
  const kpis = useMemo(() => {
    const p = filterByFam(products, famKpis);
    return {
      ruptura_stock: p.filter(x => x.stock === 0).length,
      alerta_reposicion: p.filter(x => x.alerta_reposicion).length,
      total_ingreso: p.reduce((s, x) => s + (x.ingreso || 0), 0),
      total_salida: p.reduce((s, x) => s + (x.salida || 0), 0),
      sin_movimiento: p.filter(x => (x.ingreso || 0) === 0 && (x.salida || 0) === 0).length,
    };
  }, [products, famKpis]);

  // Urgencias
  const urgencias = useMemo(() => {
    const p = filterByFam(products, famUrgencias);
    const critical = p.filter(x => x.stock === 0).slice(0, 10);
    const low = p.filter(x => x.stock > 0 && x.stock < STOCK_MIN).sort((a, b) => a.stock - b.stock).slice(0, 10 - critical.length);
    return [...critical, ...low].slice(0, 10);
  }, [products, famUrgencias]);

  // Tendencia
  const trendData = useMemo(() => {
    const e = filterByFam(entregas, famTendencia);
    const map = {};
    e.forEach(x => {
      if (!x.fecha) return;
      const key = periodoView === "mes" ? x.fecha.substring(0, 7) : x.fecha;
      if (!map[key]) map[key] = { periodo: periodoView === "mes" ? key : key.substring(5), salidas: 0, valor: 0 };
      map[key].salidas += x.cantidad || 0;
      map[key].valor += (x.cantidad || 0) * (x.pmayor || 0);
    });
    const sorted = Object.values(map).sort((a, b) => a.periodo.localeCompare(b.periodo));
    return periodoView === "dia" ? sorted.slice(-14) : sorted;
  }, [entregas, famTendencia, periodoView]);

  if (loading) {
    return <Card className="p-8 text-center border border-border"><p className="text-muted-foreground">Cargando Dashboard Operativo...</p></Card>;
  }

  const formatNum = (v) => (v || 0).toLocaleString('es-BO');
  const getSemaforo = (stock) => {
    if (stock === 0) return { color: "bg-rose-500", label: "Critico" };
    if (stock < 5) return { color: "bg-amber-500", label: "Urgente" };
    return { color: "bg-yellow-400", label: "Bajo" };
  };

  const kpiCards = [
    { title: "Ruptura de Stock", value: kpis.ruptura_stock, subtitle: "SKUs sin stock", icon: PackageX, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200" },
    { title: "Alerta Reposicion", value: kpis.alerta_reposicion, subtitle: "Sell-through >= 75%", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
    { title: "Unidades Ingresadas", value: formatNum(kpis.total_ingreso), subtitle: "Total periodo", icon: ArrowDownToLine, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { title: "Unidades Despachadas", value: formatNum(kpis.total_salida), subtitle: "Total periodo", icon: ArrowUpFromLine, color: "text-[#FF9900]", bg: "bg-[#FF9900]/10", border: "border-[#FF9900]/30" },
    { title: "Sin Movimiento", value: kpis.sin_movimiento, subtitle: "SKUs inactivos", icon: Archive, color: "text-muted-foreground", bg: "bg-muted", border: "border-border" },
  ];

  return (
    <div className="space-y-5" data-testid="dashboard-operativo">
      {/* TC Badge */}
      {globalTC !== null && globalTC !== undefined && (
        <div className="flex justify-end mb-2"><TCBadge tc={globalTC} /></div>
      )}
      {/* KPIs */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="heading-font text-sm font-semibold text-foreground">KPIs Operativos</h3>
          <FamiliaSelect value={famKpis} onChange={setFamKpis} familias={familias} testId="op-fam-kpis" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpiCards.map((k, i) => (
            <Card key={i} className={`p-4 border ${k.border} shadow-sm`} data-testid={`op-kpi-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="body-font text-xs text-muted-foreground uppercase tracking-wide">{k.title}</p>
                  <p className="heading-font text-3xl font-black text-foreground mt-1">{k.value}</p>
                  <p className="body-font text-xs text-muted-foreground mt-0.5">{k.subtitle}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${k.bg}`}><k.icon className={`h-5 w-5 ${k.color}`} /></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top 10 Urgencias */}
        <Card className="border border-border shadow-sm" data-testid="op-urgencias">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <div>
              <h3 className="heading-font text-base font-semibold text-foreground">Top 10 Urgencias de Reposicion</h3>
              <p className="body-font text-xs text-muted-foreground mt-0.5">Productos que necesitan reabastecimiento</p>
            </div>
            <FamiliaSelect value={famUrgencias} onChange={setFamUrgencias} familias={familias} testId="op-fam-urgencias" />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted">
                  <TableHead className="text-xs w-8"></TableHead>
                  <TableHead className="text-xs">Codigo</TableHead>
                  <TableHead className="text-xs">Familia</TableHead>
                  <TableHead className="text-xs text-center">Stock</TableHead>
                  <TableHead className="text-xs text-right">Accion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {urgencias.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-4 text-xs text-muted-foreground">Sin urgencias para esta familia</TableCell></TableRow>
                ) : urgencias.map((u, i) => {
                  const sem = getSemaforo(u.stock);
                  return (
                    <TableRow key={i} className="hover:bg-muted" data-testid={`urgencia-row-${i}`}>
                      <TableCell><div className={`w-3 h-3 rounded-full ${sem.color}`} title={sem.label} /></TableCell>
                      <TableCell>
                        <p className="mono-font text-xs font-medium">{u.codigo}</p>
                        <p className="body-font text-xs text-muted-foreground truncate max-w-[200px]">{u.descripcion}</p>
                      </TableCell>
                      <TableCell className="body-font text-xs">{u.familia}</TableCell>
                      <TableCell className="mono-font text-xs text-center font-bold">{u.stock}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" className="text-xs h-7 px-2.5 border-[#FF9900]/40 text-[#FF9900] hover:bg-[#FF9900]/10"
                          onClick={() => toast.info(`Pedido para ${u.codigo} (visual)`)} data-testid={`btn-pedir-${i}`}>
                          <ShoppingCart className="h-3 w-3 mr-1" /> Pedir
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Tendencia */}
        <Card className="border border-border shadow-sm" data-testid="op-tendencia">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <div>
              <h3 className="heading-font text-base font-semibold text-foreground">Tendencia de Salidas</h3>
              <p className="body-font text-xs text-muted-foreground mt-0.5">Unidades despachadas y valor</p>
            </div>
            <div className="flex items-center gap-2">
              <FamiliaSelect value={famTendencia} onChange={setFamTendencia} familias={familias} testId="op-fam-tendencia" />
              <div className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                <input type="number" step="0.01" min="0.01" value={tipoCambio} onChange={e => setTipoCambio(parseFloat(e.target.value) || 1)}
                  className="w-16 h-7 text-xs text-center border border-border rounded-md" data-testid="op-tc-input" />
              </div>
              <Select value={periodoView} onValueChange={setPeriodoView}>
                <SelectTrigger className="h-7 w-24 text-xs" data-testid="op-periodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">Por Dia</SelectItem>
                  <SelectItem value="mes">Por Mes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="p-4">
            {trendData.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-8">Sin datos de tendencia para esta familia</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="periodo" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const uds = payload.find(p => p.dataKey === 'salidas');
                    const val = payload.find(p => p.dataKey === 'valor');
                    return (
                      <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs min-w-[180px]">
                        <p className="font-semibold text-foreground mb-2 border-b pb-1">{label}</p>
                        {uds && <div className="flex justify-between gap-4 mb-1">
                          <span className="text-blue-700 font-medium">Unidades:</span>
                          <span className="font-bold">{uds.value.toLocaleString()}</span>
                        </div>}
                        {val && <div className="flex justify-between gap-4 mb-1">
                          <span className="text-orange-600 font-medium">Valor Bs:</span>
                          <span className="font-bold">{val.value.toLocaleString()}</span>
                        </div>}
                        {val && <div className="flex justify-between gap-4 text-[#FF9900]">
                          <span className="font-medium">Valor USD:</span>
                          <span className="font-bold">$ {(val.value / tipoCambio).toFixed(0)}</span>
                        </div>}
                      </div>
                    );
                  }} />
                  <Line yAxisId="left" type="monotone" dataKey="salidas" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} name="salidas" />
                  <Line yAxisId="right" type="monotone" dataKey="valor" stroke="#ea580c" strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 3, fill: '#ea580c' }} name="valor" />
                </LineChart>
              </ResponsiveContainer>
            )}
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 bg-blue-600 rounded" /><span className="text-xs text-muted-foreground font-medium">Unidades</span></div>
              <div className="flex items-center gap-1.5"><div className="w-5 h-0.5 bg-orange-600 rounded border-dashed" /><span className="text-xs text-muted-foreground font-medium">Valor Bs</span></div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardOperativo;
